<template>
  <div v-bind="$attrs" :class="{ 'phone-component': true }">
    <VueTelInput
      :model-value="phoneNumber"
      :input-options="{
        id: 'phoneNumberInputField',
        name: 'internal_phone',
        styleClasses: className,
        required,
        placeholder,
      }"
      :auto-format="true"
      :dropdown-options="dropdownOptions"
      :valid-characters-only="true"
      mode="auto"
      :preferred-countries="launchedCountries"
      :default-country="countryCode"
      @validate="validate"
      @on-input="input"
      @open="handleOpen"
    />
    <template v-if="!valid || invalidPhoneNumberError">
      <div class="tw-text-red-500 tw-my-1">
        {{
          invalidPhoneNumberError !== ''
            ? invalidPhoneNumberError
            : $t('Please enter a valid phone number.')
        }}
      </div>
    </template>
    <div
      v-for="error in graphQLErrors"
      :key="error.message"
      class="alert alert-warning text-center"
    >
      {{ error.message }}
    </div>
    <input :name="name" :value="formattedPhoneNumber" class="hidden" />
    <button
      v-if="!isVerified && showVerifyButton"
      :disabled="verifyBtnDisabled"
      class="tw-mt-2 btn btn-sm"
      @click.prevent="handleSendVerify"
    >
      {{ $t('Verify via SMS') }}
    </button>

    <span v-if="isVerified" class="label label-success">Verified</span>

    <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
    <BaseModal
      :confirm-button-text="$t('Verify')"
      :is-open="showVerifyPhoneModal"
      :save-state="verifyState"
      :saving-text="$t('Verifying') + '...'"
      :failed-text="$t('Failed, try again')"
      :can-retry="verifyCode !== null && verifyCode.length === 6"
      :disabled="verifyCode === null || verifyCode.length !== 6"
      modal-id="verifyPhoneNumberModal"
      @save="handleVerify"
      @cancel="handleModalCancel"
    >
      <template #header>
        <h4>
          {{ $t('Verify phone number') }}
        </h4>
      </template>

      <div
        v-for="error in verifyErrors"
        :key="error.message"
        class="alert alert-warning m-t-10"
        role="alert"
      >
        {{ error.message }}
      </div>

      <div class="form-group text-center p-20 m-t-20 bgm-lightgray">
        <label for="verifyField">
          {{ $t('Enter the verification code sent to your phone number via SMS') }}
        </label>
        <div class="row">
          <div class="col-sm-4 col-sm-offset-4">
            <input
              id="verifyField"
              v-model="verifyCode"
              type="text"
              pattern="\d*"
              class="form-control text-center fw"
              maxlength="6"
              minlength="6"
              placeholder="123456"
            />
          </div>
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { VueTelInput } from 'vue-tel-input'
// eslint-disable-next-line import-x/extensions
import 'vue-tel-input/vue-tel-input.css'

export default {
  components: {
    VueTelInput,
  },
  props: {
    persistentPhoneNumber: { type: String, default: '' },
    name: { type: String, default: 'Phone' },
    className: { type: String, default: 'form-control border-gray-200 overflow-hidden' },
    countryCode: { type: String, default: '' },
    required: { type: Boolean, default: false },
    initialVerifiedStatus: { type: Boolean, default: false },
    /* eslint-disable-next-line vue/no-boolean-default */
    showVerifyButton: { type: Boolean, default: true },
    invalidPhoneNumberError: { type: String, default: '' },
    placeholder: { type: String, default: 'Phone number' },
  },
  data() {
    return {
      isVerified: this.initialVerifiedStatus,
      phoneNumber: '',
      valid: false,
      formattedPhoneNumber: null,
      graphQLErrors: [],
      showVerifyPhoneModal: false,
      verifyState: 'fresh',
      verifyErrors: null,
      verifyCode: null,
      dropdownOptions: {
        showDialCodeInSelection: false,
        showFlags: true,
        showDialCodeInList: true,
        showSearchBox: true,
      },
    }
  },
  computed: {
    verifyBtnDisabled() {
      return !this.valid || this.phoneNumber === ''
    },
    launchedCountries() {
      return currentMarketCountries
    },
  },
  created() {
    if (this.persistentPhoneNumber) {
      this.phoneNumber = this.persistentPhoneNumber
    }
  },
  methods: {
    input(input, { number, formatted, valid }) {
      this.phoneNumber = valid ? formatted : input
      this.formattedPhoneNumber = number
      this.isVerified =
        this.initialVerifiedStatus &&
        input.replace(/\s/g, '') === this.persistentPhoneNumber.replace(/\s/g, '')

      this.valid = valid && input !== ''

      this.$emit('validation-updated', this.valid)
      this.$emit('phone-changed', this.formattedPhoneNumber)
    },
    validate({ number, valid }) {
      const isValid = valid || number === '' || number === undefined
      this.formattedPhoneNumber = number
      this.valid = isValid

      this.$emit('validation-updated', isValid)
      this.$emit('phone-changed', this.formattedPhoneNumber)
    },
    handleOpen() {
      this.$nextTick(() => {
        const firstItem = document.querySelector('.vti__dropdown-list li')
        firstItem?.focus()
      })
    },
    handleSendVerify() {
      this.showVerifyPhoneModal = true
      this.verifyErrors = null
      this.verifyState = 'fresh'

      this.$apollo
        .mutate({
          mutation: this.$options.sendVerify,
          variables: {
            phone: this.formattedPhoneNumber,
          },
        })
        .catch((error) => this.handleGraphQlErrors(error))
    },
    handleModalCancel() {
      this.showVerifyPhoneModal = false
    },
    handleVerify() {
      this.verifyErrors = null
      this.verifyState = 'saving'
      this.$apollo
        .mutate({
          mutation: this.$options.verifyPhone,
          variables: {
            code: this.verifyCode,
          },
        })
        .then((result) => {
          if (result.data.verifyPhone === true) {
            this.verifyState = 'saved'
            this.showVerifyPhoneModal = false
            this.isVerified = true
            /* eslint-disable-next-line vue/no-mutating-props */
            this.persistentPhoneNumber = this.phoneNumber
          }
        })
        .catch((error) => {
          this.verifyErrors = error.graphQLErrors
          this.verifyState = 'failed'
        })
    },
    handleGraphQlErrors(errors) {
      this.graphQLErrors = errors.graphQLErrors
      this.showModal = false
    },
  },
  sendVerify: gql`
    mutation SendVerify($phone: String) {
      sendVerifyPhoneSms(phone: $phone)
    }
  `,
  verifyPhone: gql`
    mutation VerifyCode($code: String!) {
      verifyPhone(code: $code)
    }
  `,
}
</script>

<style scoped lang="scss">
:deep(.phone-component) {
  input {
    padding: 14px 11px !important;
  }
}

.has-error .help-block {
  background: unset;
  color: unset;
  padding: 0;
}

.has-error .help-block:before {
  content: unset;
}

.vue-tel-input {
  border: 1px solid rgb(223 223 230);
  border-radius: 0.25rem;
}
</style>
